import { MenuPage as BaseMenuPage, Id, MenuProvider, isSuccess, menuKeys, useOpen, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { menuItemService } from "../shared/api/menuItemService";
import { MenuItemFormValues, MenuItemSaveRequest } from "../shared/types";
import { MenuItemCreateDrawer, MenuItemUpdateDrawer } from "./MenuItemDrawer";

const mapFormValueToRequest = (formValues: MenuItemFormValues): MenuItemSaveRequest => {
	return {
		type: formValues.type,
		url: formValues.url,
		children: formValues.children,
		visibility: formValues.visibility,
		translations: formValues.translations,
		blockId: formValues.block?.id ?? null,
		previewUntil: formValues.previewUntil,
		previewType: formValues.previewType,
		previewUrl: formValues.previewUrl,
		previewBlockId: formValues.previewBlock?.id ?? null,
	};
};

export const MenuPage: FC = () => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const [menuItemId, setMenuItemId] = useState<Id | null>(null);
	const {
		open: isCreateDrawerOpen,
		handleOpen: handleOpenCreateDrawer,
		handleClose: handleCloseCreateDrawer,
	} = useOpen();

	const { open: isEditDrawerOpen, handleOpen: handleOpenEditDrawer, handleClose: handleCloseEditDrawer } = useOpen();

	const handleEditMenuItem = useCallback(
		(menuItemId: Id) => {
			setMenuItemId(menuItemId);
			handleOpenEditDrawer();
		},
		[handleOpenEditDrawer],
	);

	const handleCreateMenuItem = useCallback(
		async (menuItem: MenuItemSaveRequest) => {
			const response = await menuItemService.store(menuItem);

			if (isSuccess(response)) {
				showToast({ body: t("Menu item was saved."), title: t("Save menu item"), type: "success" });
				await queryClient.invalidateQueries({ queryKey: menuKeys.index });
				await queryClient.invalidateQueries({ queryKey: menuItemService.baseKey });
				return true;
			}
			return false;
		},
		[queryClient, showToast, t],
	);

	const handleUpdateMenuItem = useCallback(
		async (menuItemId: Id, menuItem: MenuItemSaveRequest) => {
			const response = await menuItemService.update(menuItemId, menuItem);

			if (isSuccess(response)) {
				showToast({ body: t("Menu item was saved."), title: t("Save menu item"), type: "success" });
				await queryClient.invalidateQueries({ queryKey: menuKeys.index });
				await queryClient.invalidateQueries({ queryKey: menuItemService.baseKey });
				return true;
			}
			return false;
		},
		[queryClient, showToast, t],
	);

	const handleDeleteMenuItem = useCallback(
		async (menuItemId: Id) => {
			const response = await menuItemService.destroy(menuItemId);

			if (isSuccess(response)) {
				showToast({ body: t("Menu item was deleted."), title: t("Delete menu item"), type: "success" });
				await queryClient.invalidateQueries({ queryKey: menuKeys.index });
			}
		},
		[queryClient, showToast, t],
	);

	return (
		<MenuProvider>
			<BaseMenuPage onCreate={handleOpenCreateDrawer} onEdit={handleEditMenuItem} onDelete={handleDeleteMenuItem} />
			<MenuItemCreateDrawer
				open={isCreateDrawerOpen}
				onClose={handleCloseCreateDrawer}
				onClosed={() => undefined}
				onSubmit={(formValues) => handleCreateMenuItem(mapFormValueToRequest(formValues))}
			/>
			{menuItemId && (
				<MenuItemUpdateDrawer
					id={menuItemId}
					open={isEditDrawerOpen}
					onClose={handleCloseEditDrawer}
					onClosed={() => setMenuItemId(null)}
					onSubmit={(formValues) => handleUpdateMenuItem(menuItemId, mapFormValueToRequest(formValues))}
				/>
			)}
		</MenuProvider>
	);
};
